// contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() => {
    return localStorage.getItem('access_token');
  });
  const [refreshToken, setRefreshToken] = useState(() => {
    return localStorage.getItem('refresh_token');
  });
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });
  const [loading, setLoading] = useState(true);

  // Check token validity on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (accessToken) {
        try {
          // Verify token by making a simple request
          await axios.get('https://backend.dlapakistan.com/api/token/verify/', {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
        } catch (error) {
          // Token is invalid, try to refresh
          if (refreshToken) {
            try {
              const response = await axios.post('https://backend.dlapakistan.com/api/token/refresh/', {
                refresh: refreshToken
              });
              const newAccessToken = response.data.access;
              localStorage.setItem('access_token', newAccessToken);
              setAccessToken(newAccessToken);
            } catch (refreshError) {
              // Refresh failed, logout
              logout();
            }
          } else {
            logout();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = (data) => {
    localStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    setAccessToken(data.access);
    setRefreshToken(data.refresh);
    setUser(data.user);
  };

  const logout = () => {
    // Remove all auth data
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    
    // Clear state
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const isAuthenticated = !!accessToken;

  const value = {
    accessToken,
    refreshToken,
    user,
    login,
    logout,
    updateUser,
    isAuthenticated,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};